import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiErrorDetail } from "../../api";

const INPUT_CLS =
  "w-full bg-transparent border border-white/15 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 rounded-none transition-colors";

export const AuthDialog = ({ open, onOpenChange, onAuthed }) => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        toast.success("If that email is registered, a reset link is on its way.");
        setMode("login");
        return;
      }
      const u = mode === "login" ? await login(email, password) : await register(name, email, password);
      toast.success(`Welcome${u?.name ? `, ${u.name}` : ""}`);
      onOpenChange(false);
      if (onAuthed) onAuthed();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="auth-dialog" className="bg-[#121212] border border-white/10 rounded-none text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-3xl">
            {mode === "login" ? "Member Sign In" : mode === "register" ? "Join the Marketplace" : "Reset Password"}
          </DialogTitle>
          <DialogDescription className="text-white/50 text-sm">
            {mode === "login"
              ? "Sign in to post items for sale or trade."
              : mode === "register"
              ? "Create an account with your email and a password."
              : "Enter your account email and we'll send you a reset link."}
          </DialogDescription>
        </DialogHeader>

        {mode !== "forgot" && (
          <div className="grid grid-cols-2 border border-white/10 mb-6">
            <button
              type="button"
              data-testid="auth-tab-login"
              onClick={() => { setMode("login"); setError(""); }}
              className={`py-3 text-xs uppercase tracking-widest transition-colors ${mode === "login" ? "bg-primary text-white" : "text-white/50 hover:text-white"}`}
            >
              Sign In
            </button>
            <button
              type="button"
              data-testid="auth-tab-register"
              onClick={() => { setMode("register"); setError(""); }}
              className={`py-3 text-xs uppercase tracking-widest transition-colors ${mode === "register" ? "bg-primary text-white" : "text-white/50 hover:text-white"}`}
            >
              Register
            </button>
          </div>
        )}

        <form data-testid="auth-form" onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <input data-testid="auth-name-input" required minLength={2} value={name} onChange={(e) => setName(e.target.value)} placeholder="Display name" className={INPUT_CLS} />
          )}
          <input data-testid="auth-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" className={INPUT_CLS} />
          {mode !== "forgot" && (
            <input data-testid="auth-password-input" required type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (6+ characters)" className={INPUT_CLS} />
          )}
          {error && (
            <p data-testid="auth-error" className="text-sm text-red-400 border border-red-400/30 bg-red-400/10 px-4 py-3">{error}</p>
          )}
          <button
            type="submit"
            data-testid="auth-submit-btn"
            disabled={busy}
            className="w-full bg-primary text-white px-8 py-3.5 uppercase tracking-widest text-sm font-semibold hover:bg-[#FF7733] transition-colors disabled:opacity-50"
          >
            {busy ? "Working..." : mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : "Send Reset Link"}
          </button>
          {mode === "login" && (
            <button
              type="button"
              data-testid="auth-forgot-link"
              onClick={() => { setMode("forgot"); setError(""); }}
              className="w-full text-center text-xs uppercase tracking-widest text-violet-400 hover:text-violet-300 transition-colors pt-1"
            >
              Forgot your password?
            </button>
          )}
          {mode === "forgot" && (
            <button
              type="button"
              data-testid="auth-back-login"
              onClick={() => { setMode("login"); setError(""); }}
              className="w-full text-center text-xs uppercase tracking-widest text-white/50 hover:text-white transition-colors pt-1"
            >
              Back to sign in
            </button>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};
